import { ArrowLeft, ArrowRight, Check, CircleAlert, Eye, EyeOff, Info, Minus, Plus, ShieldCheck, Wallet } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthContext";
import { AutofillUsername } from "../../components/AutofillUsername";
import { KUSPIT_LEGAL_LINK_LABEL, KUSPIT_WALLET_NOTICE } from "@aventuria/client-core/disclosures";
import { ButtonLabel } from "../../components/ButtonLabel";
import { KuspitNotice } from "../../components/Disclosures";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { ApiError, apiRequest, describeApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { useLibreWallet, useMyRequest, useReservationConfig } from "../../lib/queries";
import { mxn } from "../../lib/reservations";
import type { Apartado, ApiEnvelope, User } from "../../types";
import { InvestmentInfo } from "./InvestmentInfo";

/**
 * Flow C — the Apartado offer ("Organiza los pagos de este viaje") and the funding screen (6a steps
 * 2–3). Origin, destination, minimum, amount and projected balances are visible before the transfer
 * (FR10); the final CTA is enabled only with a valid amount, both consents and enough balance
 * (C3); creation and funding are one atomic API call, idempotent on retry (AC15).
 */
export function ApartadoCreatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const detail = useMyRequest(id);
  const { wallet, isLoading: walletLoading } = useLibreWallet();
  const config = useReservationConfig();
  const [step, setStep] = useState<"offer" | "fund" | "info">("offer");
  usePageHeader(step === "offer" ? "Crea tu Apartado" : step === "fund" ? "Transferencia a tu Apartado" : "Cómo se invierte tu dinero", detail.data ? detail.data.request.trip.name : undefined);

  if (detail.isLoading || walletLoading || config.isLoading) return <div className="page-state"><Spinner label="Preparando tu Apartado" /><span>Preparando tu Apartado</span></div>;
  if (detail.isError || !detail.data || !config.data) {
    return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No encontramos esta solicitud</strong><button className="secondary-button" onClick={() => navigate("/solicitudes")}>Ir a Mis solicitudes</button></div>;
  }
  const { request, apartadoEligibility: eligibility } = detail.data;
  if (!eligibility.eligible) {
    return (
      <div className="page-container">
        <section className="card">
          <span className="page-eyebrow">Apartado</span>
          <h2>{eligibility.existingApartadoId ? "Ya tienes un Apartado para este viaje" : "Este viaje no admite un Apartado ahora"}</h2>
          <p>{eligibility.message}</p>
          <div className="inline-actions">
            {eligibility.existingApartadoId && <button type="button" className="primary-button solid" onClick={() => navigate(`/apartados/${eligibility.existingApartadoId}`)}>Ver apartado</button>}
            <button type="button" className="secondary-button" onClick={() => navigate(`/solicitudes?sel=${request.id}`)}>Volver a mi solicitud</button>
          </div>
        </section>
      </div>
    );
  }
  const minimum = eligibility.minimumContribution;
  const goal = eligibility.goalAmount ?? minimum;
  const free = wallet?.balance.free ?? 0;
  const shortfall = Math.max(0, minimum - free);
  const perPassenger = minimum / Math.max(1, eligibility.travelers);

  if (step === "info") {
    return (
      <div className="page-container apartado-flow">
        <button type="button" className="back-button" onClick={() => setStep("fund")}><ArrowLeft size={16} aria-hidden="true" /> Volver</button>
        <InvestmentInfo fundLabel={config.data.investment.fundLabel} infoText={config.data.investment.infoText} prospectusUrl={config.data.prospectusUrl} fundInfoUrl={config.data.fundInfoUrl} />
      </div>
    );
  }

  if (step === "offer") {
    return (
      <div className="page-container apartado-flow">
        <button type="button" className="back-button" onClick={() => navigate(`/solicitudes/${request.id}`)}><ArrowLeft size={16} aria-hidden="true" /> Mi solicitud</button>
        <section className="card offer-detail">
          <span className="tag">Opcional</span>
          <h2>Organiza los pagos de este viaje</h2>
          <p>Crea un Apartado exclusivo para {request.trip.name}. La aportación inicial requerida es de <strong translate="no">{mxn(minimum)}</strong> y puedes continuar abonando desde Wallet Libre.</p>
          <div className="inline-notice info"><Info size={16} aria-hidden="true" /> Tu solicitud de reservación seguirá su curso aunque decidas crear tu Apartado después.</div>
          <dl className="kv-list offer-facts">
            <div><dt>Viaje</dt><dd>{request.trip.name}</dd></div>
            <div><dt>Meta del viaje</dt><dd translate="no">{mxn(goal)}</dd></div>
            <div><dt>Aportación inicial requerida</dt><dd translate="no">{mxn(minimum)} <small>({eligibility.travelers} × {mxn(perPassenger).replace(".00", "")})</small></dd></div>
          </dl>
          <div className="offer-benefits">
            <div><span className="benefit-icon"><Wallet size={17} aria-hidden="true" /></span><div><strong>Abona a tu ritmo</strong><p>Después de la aportación inicial, continúa abonando desde Wallet Libre.</p></div></div>
          </div>
          <p className="offer-warning"><ShieldCheck size={15} aria-hidden="true" /> <strong>Importante:</strong> el dinero que transfieras quedará destinado al pago de este viaje y ya no podrá retirarse.</p>
          {!wallet && (
            <div className="form-error" role="alert">
              Necesitas una Wallet Libre para crear tu Apartado. <button type="button" className="link-button" onClick={() => navigate("/wallet/libre/nueva")}>Abrir mi Wallet Libre</button>
            </div>
          )}
          {wallet && shortfall > 0 && (
            <div className="form-error" role="alert">
              <strong>Saldo insuficiente para crear este Apartado.</strong> Necesitas <span translate="no">{mxn(shortfall)}</span> adicionales en tu Wallet Libre.
              <button type="button" className="link-button" onClick={() => navigate("/wallet/libre", { state: { fund: true } })}>¿Cómo deposito en Wallet Libre?</button>
            </div>
          )}
          <div className="inline-actions">
            <button type="button" className="primary-button solid" onClick={() => setStep("fund")} disabled={!wallet || shortfall > 0}>Continuar a transferencia <ArrowRight size={16} aria-hidden="true" /></button>
            <button type="button" className="link-button" onClick={() => navigate(`/solicitudes?sel=${request.id}`)}>Quizá después</button>
          </div>
          <KuspitNotice />
        </section>
      </div>
    );
  }

  return (
    <div className="page-container apartado-flow">
      <button type="button" className="back-button" onClick={() => setStep("offer")}><ArrowLeft size={16} aria-hidden="true" /> Volver</button>
      <FundingForm
        requestId={request.id} tripName={request.trip.name} minimum={minimum} goal={goal} walletId={wallet!.id} free={free}
        consentVersion={config.data.consentVersion} documentsVersion={config.data.documentsVersion} fundLabel={config.data.investment.fundLabel}
        onInfo={() => setStep("info")} onCancel={() => navigate(`/solicitudes?sel=${request.id}`)}
        onCreated={(apartado) => navigate(`/apartados/${apartado.id}`, { state: { created: true, amount: apartado.balance } })}
      />
    </div>
  );
}

/** The funding screen (C2/C3), shared with "Añadir fondos" on Mi Apartado (C4). */
export function FundingForm({ requestId, apartadoId, tripName, minimum, goal, walletId, free, currentBalance = 0, consentVersion, documentsVersion, fundLabel, onInfo, onCancel, onCreated }: {
  requestId?: string;
  apartadoId?: string;
  tripName: string;
  minimum: number;
  goal: number;
  walletId: string;
  free: number;
  currentBalance?: number;
  consentVersion: string;
  documentsVersion: string;
  fundLabel: string;
  onInfo(): void;
  onCancel(): void;
  onCreated(apartado: Apartado): void;
}) {
  const { user, applyUser } = useAuth();
  const queryClient = useQueryClient();
  const creating = !apartadoId;
  const [amount, setAmount] = useState(Math.min(free, Math.max(minimum, creating ? minimum : Math.min(free, 1000))));
  const [ack, setAck] = useState(false);
  const [docs, setDocs] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const needsPassword = user?.hasPassword === false;
  const step = 500;
  const insufficient = free < minimum;
  useEffect(() => { void walletId; }, [walletId]);

  const hint = useMemo(() => {
    if (amount <= 0) return { tone: "warn", text: "Ingresa un monto mayor a $0.00 para continuar." };
    if (creating && amount < minimum) return { tone: "warn", text: `El monto no puede ser menor a la aportación mínima requerida de ${mxn(minimum)}.` };
    if (amount > free) return { tone: "bad", text: "El monto excede tu saldo disponible en Wallet Libre." };
    return { tone: "ok", text: `Después de transferir, tu Apartado tendrá ${mxn(currentBalance + amount)}.` };
  }, [amount, minimum, free, creating, currentBalance]);
  const valid = amount > 0 && amount <= free && (!creating || amount >= minimum) && ack && (creating ? docs : true) && !insufficient && (password.length > 0 || false);
  const clamp = (value: number) => Math.max(0, Math.min(free, Math.round(value * 100) / 100));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setSubmitting(true); setError(null);
    track("apartado_transfer_started", { request_id: requestId, amount, available_balance: free });
    try {
      if (needsPassword) {
        const created = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/auth/password/set", { method: "POST", body: JSON.stringify({ password }) });
        applyUser(created.data.user);
      }
      const stepUp = await apiRequest<ApiEnvelope<{ stepUpToken: string }>>("/api/v1/auth/step-up", { method: "POST", body: JSON.stringify({ password }) });
      const headers = { "idempotency-key": idempotencyKey, "x-step-up-token": stepUp.data.stepUpToken };
      const payload = creating
        ? await apiRequest<ApiEnvelope<{ apartado: Apartado }>>("/api/v1/apartados", {
          method: "POST", headers,
          body: JSON.stringify({ requestId, amount, consentAccepted: true, documentsAccepted: true, consentVersion, documentsVersion })
        })
        : await apiRequest<ApiEnvelope<{ apartado: Apartado }>>(`/api/v1/apartados/${apartadoId}/contributions`, {
          method: "POST", headers,
          body: JSON.stringify({ amount, consentAccepted: true, consentVersion })
        });
      setIdempotencyKey(crypto.randomUUID());
      await Promise.all([queryClient.invalidateQueries({ queryKey: ["wallets"] }), queryClient.invalidateQueries({ queryKey: ["apartados"] }), queryClient.invalidateQueries({ queryKey: ["reservations"] })]);
      onCreated(payload.data.apartado);
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : "NETWORK";
      track("apartado_transfer_failed", { error_category: code, retryable: code !== "APARTADO_EXISTS" && code !== "CONSENT_OUTDATED" });
      if (cause instanceof ApiError && cause.code === "APARTADO_EXISTS") {
        const existing = (cause.details as { existingApartadoId?: string } | undefined)?.existingApartadoId;
        setError([`${cause.message} ${existing ? "Abre tu Apartado para continuar." : ""}`]);
      } else if (cause instanceof ApiError && cause.code === "INSUFFICIENT_FUNDS") {
        const details = cause.details as { free?: number } | undefined;
        setError([`${cause.message} Recalculamos tu saldo disponible${details?.free !== undefined ? `: ${mxn(details.free)}` : ""}. Revisa el monto y vuelve a confirmar.`]);
        void queryClient.invalidateQueries({ queryKey: ["wallets"] });
      } else {
        setError(describeApiError(cause, "No pudimos completar la transferencia. Tu Wallet Libre no cambió; puedes intentar de nuevo."));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <form className="card funding-card" onSubmit={submit}>
      <AutofillUsername />
      <span className="page-eyebrow">{creating ? "Creación y transferencia" : "Añadir fondos"}</span>
      <h2>{creating ? "Crea tu Apartado y haz tu primera aportación" : "Añade fondos a tu Apartado"}</h2>
      <p>{creating ? "El Apartado se crea junto con tu transferencia desde Wallet Libre. Si la transferencia no se completa, no se crea ningún Apartado." : "Transfiere desde Wallet Libre. Cada abono acerca tu Apartado a la meta del viaje."}</p>

      <div className="transfer-legs">
        <div className="transfer-leg"><small>Origen</small><strong>Wallet Libre</strong><span translate="no">Disponible {mxn(free)}</span></div>
        <ArrowRight size={18} aria-hidden="true" className="transfer-arrow" />
        <div className="transfer-leg"><small>Destino</small><strong>Apartado para {tripName}</strong><span translate="no">{creating ? `Mínimo inicial ${mxn(minimum)}` : `Saldo actual ${mxn(currentBalance)}`} · meta {mxn(goal)}</span></div>
      </div>

      <div className="amount-display">
        <label className="field-label" htmlFor="apartado-amount">Monto a transferir</label>
        <span className="amount-hint" translate="no">{creating ? `Entre ${mxn(minimum)} y ${mxn(free)}` : `Hasta ${mxn(free)}`}</span>
        <div className="amount-stepper">
          <button type="button" onClick={() => setAmount(clamp(amount - step))} disabled={amount <= (creating ? minimum : 0)} aria-label="Restar 500 pesos"><Minus size={20} aria-hidden="true" /></button>
          <div className="money-field">
            <span aria-hidden="true">$</span>
            <input id="apartado-amount" inputMode="decimal" autoComplete="transaction-amount" value={amount ? String(amount) : ""} onChange={(event) => setAmount(clamp(Number(event.target.value.replace(/[^\d.]/g, "")) || 0))} placeholder="0" />
            <span>MXN</span>
          </div>
          <button type="button" onClick={() => setAmount(clamp(amount + step))} disabled={amount + step > free} aria-label="Sumar 500 pesos"><Plus size={20} aria-hidden="true" /></button>
        </div>
        <div className="preset-grid three">
          {creating && <button type="button" className={`preset-chip${amount === minimum ? " active" : ""}`} onClick={() => setAmount(clamp(minimum))} disabled={minimum > free}>Mínimo</button>}
          {!creating && [1000, 2500, 5000].map((preset) => <button key={preset} type="button" className={`preset-chip${amount === preset ? " active" : ""}`} onClick={() => setAmount(clamp(preset))} disabled={preset > free}>${preset.toLocaleString("es-MX")}</button>)}
          <button type="button" className={`preset-chip${amount === free && free > 0 ? " active" : ""}`} onClick={() => setAmount(clamp(free))} disabled={free <= 0}>Todo mi saldo</button>
        </div>
        <span className={`amount-status ${hint.tone}`} role="status">{hint.tone === "ok" ? <Info size={14} aria-hidden="true" /> : <CircleAlert size={14} aria-hidden="true" />} {hint.text}</span>
      </div>

      <div className="after-grid">
        <div><small>Wallet Libre después</small><strong translate="no">{mxn(Math.max(0, free - amount))}</strong></div>
        <div><small>Apartado después</small><strong translate="no">{mxn(currentBalance + amount)}</strong></div>
      </div>

      {insufficient && (
        <div className="form-error" role="alert"><strong>Saldo insuficiente para {creating ? "crear este Apartado" : "abonar"}.</strong> Necesitas <span translate="no">{mxn(minimum - free)}</span> adicionales en tu Wallet Libre. <a className="link-button" href="/wallet/libre">¿Cómo deposito en Wallet Libre?</a></div>
      )}

      <label className={`consent-row${ack ? " checked" : ""}`}>
        <input type="checkbox" checked={ack} onChange={(event) => setAck(event.target.checked)} />
        <span className="consent-box" aria-hidden="true">{ack && <Check size={13} strokeWidth={3} />}</span>
        <span><strong>Entiendo que el dinero transferido quedará destinado al pago de este viaje y no podrá retirarse.</strong></span>
      </label>
      {creating && (
        <label className={`consent-row${docs ? " checked" : ""}`}>
          <input type="checkbox" checked={docs} onChange={(event) => setDocs(event.target.checked)} />
          <span className="consent-box" aria-hidden="true">{docs && <Check size={13} strokeWidth={3} />}</span>
          <span>He leído y acepto el <button type="button" className="link-button" onClick={onInfo}>prospecto y la documentación del fondo</button>, conozco los riesgos y autorizo la inversión de mis recursos conforme a sus términos.</span>
        </label>
      )}
      <p className="resv-fine"><ShieldCheck size={13} aria-hidden="true" /> Tu dinero permanece invertido en {fundLabel} a través de Kuspit; el Apartado registra qué parte queda destinada a este viaje. <button type="button" className="link-button" onClick={onInfo}>Más información sobre tu inversión</button></p>

      <label htmlFor="apartado-password">{needsPassword ? "Crea una contraseña para confirmar" : "Confirma tu contraseña"}</label>
      <div className="password-field">
        <input id="apartado-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={needsPassword ? "new-password" : "current-password"} minLength={needsPassword ? 12 : undefined} required />
        <button type="button" className="field-icon" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>{showPassword ? <EyeOff size={19} aria-hidden="true" /> : <Eye size={19} aria-hidden="true" />}</button>
      </div>
      {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
      <div className="inline-actions">
        <button type="submit" className="primary-button solid" disabled={!valid || submitting}>
          <ButtonLabel busy={submitting} busyLabel={creating ? "Creando tu Apartado" : "Transfiriendo"} label={creating ? `Crear Apartado y transferir ${mxn(amount)}` : `Transferir ${mxn(amount)}`} />
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={submitting}>Cancelar</button>
        <span className="resv-helper">{creating ? "Si cancelas, no se crea ningún Apartado ni se mueve dinero." : "Puedes cancelar sin mover dinero."}</span>
      </div>
      <p className="wallet-legal legal-notice">
        {KUSPIT_WALLET_NOTICE}{" "}
        <button type="button" className="legal-link" onClick={onInfo}>{KUSPIT_LEGAL_LINK_LABEL}</button>
      </p>
    </form>
  );
}
